<template>
  <div class="landing-wrapper">
    <div class="stage-floor"></div>

    <v-container class="landing-main px-4 px-sm-6 px-md-10 py-8 py-md-12" fluid>
      <section class="reserve-view">
        <header class="reserve-head">
          <h2 class="reserve-head-title">AGENDA LAS FECHAS DE TU RESERVA</h2>
          <p class="reserve-head-sub">SELECCIONA EN EL CALENDARIO LOS DÍAS QUE DESEAS AGENDAR</p>
        </header>

        <div class="reserve-body">
          <div class="reserve-detail" v-if="show">
            <h3 class="show-detail-title">{{ show.title }}</h3>
            <p class="show-detail-text">{{ show.synopsis }}</p>

            <div class="show-detail-facts">
              <div class="fact-row" v-for="fact in show.facts" :key="fact.label">
                <span class="fact-label">{{ fact.label }}</span>
                <span class="fact-value">{{ fact.value }}</span>
              </div>
              <div class="fact-row fact-price" v-if="show.price">
                <span class="fact-label">VALOR FUNCIÓN</span>
                <span class="fact-value">{{ show.price }}</span>
              </div>
              <!-- Precio para funciones fuera de la Región Metropolitana. -->
              <div class="fact-row fact-price" v-if="hasRegionPrice">
                <span class="fact-label">VALOR FUERA DE LA REGIÓN</span>
                <span class="fact-value">{{ show.priceRegionDisplay }}</span>
              </div>
            </div>
            <p v-if="hasRegionPrice" class="reserve-region-note">
              * El valor fuera de la región aplica a funciones fuera de la Región
              Metropolitana. Si tu función es en regiones, indícalo en el comentario
              de la reserva; la administración lo confirmará en el contrato.
            </p>

            <v-btn class="back-link detail-back" variant="text" prepend-icon="mdi-undo" @click="goBack">VOLVER</v-btn>
          </div>

          <div class="reserve-calendar-col">
            <div class="calendar-card">
              <Datepicker
                v-model="selectedDate"
                inline
                auto-apply
                :enable-time-picker="false"
                :min-date="minDate"
                :max-date="maxDate"
                :disabled-dates="disabledDates"
                locale="es"
                week-start="1"
              />
            </div>

            <!-- Qué significa cada color del calendario. -->
            <ul class="cal-legend">
              <li>
                <span class="cal-legend-dot cal-legend-free"></span>
                DISPONIBLE
              </li>
              <li>
                <span class="cal-legend-dot cal-legend-busy"></span>
                OCUPADO
              </li>
              <li>
                <span class="cal-legend-dot cal-legend-selected"></span>
                TU SELECCIÓN
              </li>
            </ul>

            <p v-if="selectedDate" class="reserve-selected-count">
              Fecha seleccionada: {{ formatDate(selectedDate) }}
            </p>
            <p v-else class="reserve-selected-count">
              Selecciona una fecha en el calendario para continuar.
            </p>

            <!-- Comentario para la administración (sólo ella lo ve). -->
            <div v-if="selectedDate" class="reserve-comment">
              <label class="reserve-comment-label" for="reserve-comment">
                ¿Quieres agregar un comentario respecto a la reserva? (más de una función, ubicación fuera de Santiago, servicios extras):
              </label>
              <textarea
                id="reserve-comment"
                v-model="comment"
                class="reserve-comment-input"
                rows="4"
                maxlength="2000"
              ></textarea>
            </div>

            <v-btn
              class="solicitar-btn"
              color="red-darken-3"
              rounded="lg"
              :disabled="!selectedDate"
              @click="submitReserve"
            >
              SOLICITAR RESERVA
            </v-btn>
          </div>
        </div>
      </section>
    </v-container>

    <transition name="video-fade">
      <div v-if="loginPromptOpen" class="video-overlay" @click.self="closeLoginPrompt">
        <div class="contact-dialog">
          <button type="button" class="video-close contact-close" aria-label="Cerrar" @click="closeLoginPrompt">✕</button>
          <h3 class="contact-title">INICIA SESIÓN</h3>
          <p class="contact-sub">Debes iniciar sesión para solicitar la reserva de "{{ show.title }}"</p>
          <v-btn class="solicitar-btn contact-submit" color="red-darken-3" rounded="lg" @click="goLogin">
            IR A LOGIN
          </v-btn>
        </div>
      </div>
    </transition>

    <!-- Faltan datos de la empresa: la reserva no se puede facturar sin ellos. -->
    <transition name="video-fade">
      <div v-if="missingDataOpen" class="video-overlay" @click.self="closeMissingData">
        <div class="contact-dialog">
          <button type="button" class="video-close contact-close" aria-label="Cerrar" @click="closeMissingData">✕</button>
          <h3 class="contact-title">FALTAN DATOS DE TU EMPRESA</h3>
          <p class="contact-sub">{{ missingDataMessage }}</p>
          <ul v-if="missingData.length" class="missing-list">
            <li v-for="item in missingData" :key="item">{{ item }}</li>
          </ul>
          <v-btn class="solicitar-btn contact-submit" color="red-darken-3" rounded="lg" @click="goCompanyProfile">
            IR A MI EMPRESA
          </v-btn>
        </div>
      </div>
    </transition>

    <!-- Formulario de aceptación de términos: la reserva SOLO se genera aquí. -->
    <transition name="video-fade">
      <div v-if="termsPromptOpen" class="video-overlay" @click.self="closeTermsPrompt">
        <div class="contact-dialog">
          <button type="button" class="video-close contact-close" aria-label="Cerrar" @click="closeTermsPrompt">✕</button>
          <h3 class="contact-title">CONFIRMA TU RESERVA</h3>
          <p class="contact-sub">
            Estás por solicitar la reserva de "{{ show.title }}"
            <template v-if="selectedDate"> para el {{ formatDate(selectedDate) }}</template>.
          </p>

          <label class="terms-check">
            <input type="checkbox" v-model="acceptedTerms" />
            <span>
              He leído y acepto los
              <a href="#" class="terms-link" @click.prevent="openTermsText">términos y condiciones</a>.
            </span>
          </label>

          <v-btn
            class="solicitar-btn contact-submit"
            color="red-darken-3"
            rounded="lg"
            :disabled="!acceptedTerms || enviando"
            @click="acceptAndReserve"
          >
            {{ enviando ? 'ENVIANDO…' : 'CONFIRMAR RESERVA' }}
          </v-btn>
        </div>
      </div>
    </transition>

    <!-- Reserva enviada: el único camino es ir al detalle de la reserva. -->
    <transition name="video-fade">
      <div v-if="successOpen" class="video-overlay">
        <div class="contact-dialog result-dialog">
          <v-icon class="result-icon result-icon-ok" size="54">mdi-check-circle-outline</v-icon>
          <h3 class="contact-title">¡SOLICITUD ENVIADA!</h3>
          <p class="contact-sub">
            Recibimos tu solicitud de reserva de "{{ success.title }}"
            <template v-if="success.date"> para el {{ success.date }}</template>.
            Te avisaremos por correo cuando confirmemos la fecha.
          </p>
          <v-btn class="solicitar-btn contact-submit" color="red-darken-3" rounded="lg" @click="goReservationDetail">
            OK, VER MI RESERVA
          </v-btn>
        </div>
      </div>
    </transition>

    <!-- No se pudo enviar la reserva. -->
    <transition name="video-fade">
      <div v-if="errorOpen" class="video-overlay" @click.self="closeError">
        <div class="contact-dialog result-dialog">
          <button type="button" class="video-close contact-close" aria-label="Cerrar" @click="closeError">✕</button>
          <v-icon class="result-icon result-icon-error" size="54">mdi-alert-circle-outline</v-icon>
          <h3 class="contact-title">NO SE PUDO ENVIAR</h3>
          <p class="contact-sub">{{ errorMessage }}</p>
          <v-btn class="solicitar-btn contact-submit" color="red-darken-3" rounded="lg" @click="closeError">
            ENTENDIDO
          </v-btn>
        </div>
      </div>
    </transition>

    <!-- Modal con el texto de los términos y condiciones. -->
    <transition name="video-fade">
      <div v-if="termsTextOpen" class="video-overlay terms-text-overlay" @click.self="closeTermsText">
        <div class="contact-dialog terms-dialog">
          <button type="button" class="video-close contact-close" aria-label="Cerrar" @click="closeTermsText">✕</button>
          <h3 class="contact-title">TÉRMINOS Y CONDICIONES</h3>
          <div class="terms-links">
            <a href="/terminos-y-condiciones/" target="_blank" rel="noopener">Abrir en una pestaña</a>
            <a v-if="terms.pdf_url" :href="terms.pdf_url" target="_blank" rel="noopener">Descargar PDF</a>
          </div>
          <div class="terms-body">
            <div v-if="termsLoading" class="terms-loading">
              <v-progress-circular indeterminate color="#c8102e" size="28" />
            </div>
            <p v-else-if="termsError" class="terms-error">
              No pudimos cargar el documento. Puedes leerlo en
              <a href="/terminos-y-condiciones/" target="_blank" rel="noopener">esta página</a>.
            </p>
            <terms-content v-else :blocks="terms.blocks" />
          </div>
          <v-btn class="solicitar-btn contact-submit" color="red-darken-3" rounded="lg" @click="acceptFromText">
            ACEPTAR Y CONTINUAR
          </v-btn>
        </div>
      </div>
    </transition>

    <teatro-footer />
  </div>
</template>

<script>
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { getShowById, detailContent } from "./teatroData";
import {
  fetchPlay,
  createReservation,
  fetchMe,
  fetchReservationRequirements,
  fetchTerms,
} from "@/services/teatroApi";
import TermsContent from "./TermsContent.vue";

// Texto de términos y condiciones (placeholder). Reemplazar por el texto real
// cuando esté disponible; cada elemento del arreglo es un párrafo.
export default {
  name: "ReserveShow",
  components: { Datepicker, TermsContent },
  props: {
    // Llega desde la URL /reservar/<id>/ vía template_vue de Django.
    obraId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      show: null,
      enviando: false,
      // Objeto Date único (v-model de vue-datepicker), o null si no hay selección.
      selectedDate: null,
      // Comentario libre del cliente sobre la reserva.
      comment: "",
      today: new Date(),
      loginPromptOpen: false,
      isAuthenticated: false,
      me: null,
      // El RUT de la reserva es el de la empresa (razón social), no el de la
      // persona, así que sin los datos de la empresa no se puede reservar.
      missingDataOpen: false,
      missingData: [],
      missingDataMessage: "",
      // Términos y condiciones: el documento lo entrega /api/terminos/, así el
      // modal muestra exactamente lo mismo que /terminos-y-condiciones/.
      termsPromptOpen: false,
      termsTextOpen: false,
      acceptedTerms: false,
      terms: { blocks: [], pdf_url: "" },
      termsLoading: false,
      termsError: false,
      // Resultado del envío de la reserva.
      successOpen: false,
      success: { id: null, title: "", date: "" },
      errorOpen: false,
      errorMessage: "",
    };
  },
  async created() {
    try {
      this.show = await fetchPlay(this.obraId);
    } catch (e) {
      console.error("No se pudo cargar la obra desde el backend:", e);
    }
    if (!this.show) {
      // Fallback a los datos locales.
      const local = getShowById(this.obraId);
      this.show = {
        ...local,
        synopsis: detailContent.synopsis,
        facts: detailContent.facts,
        blockedDates: [],
      };
    }
    // Verificar si el usuario está logeado y cargar sus datos.
    try {
      await this.$store.dispatch("fetchIsAuthenticated");
      this.isAuthenticated = this.$store.state.is_authenticated;
      if (this.isAuthenticated) {
        this.me = await fetchMe();
      }
    } catch (e) {
      console.error("No se pudo verificar la sesión:", e);
    }
  },
  computed: {
    hasRegionPrice() {
      return !!(this.show && Number(this.show.priceRegion) > 0);
    },
    // No se puede agendar antes de hoy ni antes del inicio de la temporada.
    minDate() {
      const start = this.show && this.show.startDate ? new Date(this.show.startDate) : null;
      if (start && start > this.today) return start;
      return this.today;
    },
    // Fin de la temporada de la obra (si tiene).
    maxDate() {
      return this.show && this.show.endDate ? new Date(this.show.endDate) : null;
    },
    // Fechas bloqueadas (reservas confirmadas + bloqueos) como objetos Date.
    disabledDates() {
      const blocked = (this.show && this.show.blockedDates) || [];
      return blocked.map((d) => new Date(`${d}T00:00:00`));
    },
  },
  methods: {
    formatDate(date) {
      // Date -> "dd/mm/yyyy" para mostrar.
      if (!date) return "";
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${d}/${m}/${y}`;
    },
    toISODate(date) {
      // Date -> "YYYY-MM-DD" para enviar al backend.
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },
    async submitReserve() {
      if (!this.selectedDate) {
        return;
      }
      if (!this.isAuthenticated) {
        this.loginPromptOpen = true;
        return;
      }
      // Se avisa acá y no al enviar: no tiene sentido hacerle aceptar los
      // términos para después rechazarle la reserva por datos que le faltan.
      if (!(await this.companyDataReady())) return;
      // Abrir el formulario de aceptación: la reserva no se genera todavía.
      this.acceptedTerms = false;
      this.termsTextOpen = false;
      this.termsPromptOpen = true;
    },
    // True si la empresa del cliente tiene RUT, teléfono y correo registrados.
    async companyDataReady() {
      try {
        const data = await fetchReservationRequirements();
        if (data.ready) return true;
        this.showMissingData(data.missing, data.message);
        return false;
      } catch (e) {
        // Si la consulta falla no se bloquea la reserva: el backend vuelve a
        // validar al recibir el POST, que es donde la regla se hace cumplir.
        console.error("No se pudieron verificar los datos de la empresa:", e);
        return true;
      }
    },
    showMissingData(missing, message) {
      this.missingData = missing || [];
      this.missingDataMessage =
        message ||
        "Faltan datos en el perfil de tu empresa para poder generar la reserva.";
      this.termsPromptOpen = false;
      this.missingDataOpen = true;
    },
    closeMissingData() {
      this.missingDataOpen = false;
    },
    goCompanyProfile() {
      window.location.href = "/mi-compania/";
    },
    closeLoginPrompt() {
      this.loginPromptOpen = false;
    },
    goLogin() {
      window.location.href = `/login/?next=/reservar/${this.obraId}/`;
    },
    closeTermsPrompt() {
      this.termsPromptOpen = false;
    },
    async openTermsText() {
      this.termsTextOpen = true;
      // Se pide al abrirlo y una sola vez: son 15 páginas y no todos las abren.
      if (this.terms.blocks.length || this.termsLoading) return;
      this.termsLoading = true;
      this.termsError = false;
      try {
        this.terms = await fetchTerms();
      } catch (e) {
        console.error("No se pudieron cargar los términos y condiciones:", e);
        this.termsError = true;
      } finally {
        this.termsLoading = false;
      }
    },
    closeTermsText() {
      this.termsTextOpen = false;
    },
    // Aceptar desde el modal de texto: marca la casilla y vuelve al formulario.
    acceptFromText() {
      this.acceptedTerms = true;
      this.termsTextOpen = false;
    },
    // Único punto donde se genera la reserva, tras aceptar los términos.
    async acceptAndReserve() {
      if (!this.acceptedTerms || this.enviando) return;
      await this.confirmReserve();
    },
    async confirmReserve() {
      if (this.enviando) return;
      this.enviando = true;
      const payload = {
        play: this.show.id,
        name: this.me.fullName || `${this.me.firstName || ""} ${this.me.lastName || ""}`.trim(),
        // El RUT no se manda: el backend usa el de la empresa del cliente.
        phone: this.me.phone || "",
        email: this.me.email || "",
        dates: [this.toISODate(this.selectedDate)],
        client_comment: this.comment.trim(),
      };
      try {
        const reservation = await createReservation(payload);
        this.success = {
          id: reservation && reservation.id,
          title: this.show.title,
          date: this.formatDate(this.selectedDate),
        };
        this.selectedDate = null;
        this.comment = "";
        this.termsPromptOpen = false;
        this.acceptedTerms = false;
        this.successOpen = true;
      } catch (e) {
        console.error("Error al enviar la reserva:", e);
        const data = e.response?.data;
        // El backend revalida: si le faltan datos a la empresa, se muestra el
        // mismo aviso en vez de un "no se pudo enviar" a ciegas.
        if (data?.company_data) {
          this.showMissingData(data.company_data, data.detail);
        } else {
          this.errorMessage =
            data?.detail ||
            (Array.isArray(data) && data[0]) ||
            "No se pudo enviar la reserva. Intenta nuevamente.";
          this.termsPromptOpen = false;
          this.errorOpen = true;
        }
      } finally {
        this.enviando = false;
      }
    },
    goReservationDetail() {
      // Sin id (respuesta inesperada) se va al listado de reservas.
      window.location.href = this.success.id ? `/reserva/${this.success.id}/` : "/mis-reservas/";
    },
    closeError() {
      this.errorOpen = false;
    },
    goBack() {
      window.location.href = `/obra/${this.show.id}/`;
    },
  },
};
</script>

<style scoped>
.reserve-region-note {
  margin: 10px 0 0;
  color: var(--color-theater-text-muted);
  font-size: 11px;
  line-height: 1.5;
}

/* Comentario del cliente bajo el calendario. */
.reserve-comment {
  margin: 0 0 16px;
  text-align: left;
}

.reserve-comment-label {
  display: block;
  margin: 0 0 8px;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--color-theater-text);
}

.reserve-comment-input {
  display: block;
  width: 100%;
  resize: vertical;
  padding: 10px 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: #fff;
  color: #1a1a1a;
  font: inherit;
  font-size: 13px;
  line-height: 1.5;
}

.reserve-comment-input:focus {
  outline: none;
  border-color: #c8102e;
}

/* Resultado del envío de la reserva (éxito / error). */
.result-dialog {
  text-align: center;
}

.result-icon {
  display: block;
  margin: 0 auto 10px;
}

.result-icon-ok {
  color: #40b04c !important;
}

.result-icon-error {
  color: #c8102e !important;
}

/* Casilla de aceptación de términos. */
.terms-links {
  display: flex;
  justify-content: center;
  gap: 18px;
  margin: 0 0 14px;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
}

.terms-links a {
  color: #ff8a9c;
  text-decoration: none;
}

.terms-links a:hover {
  color: #fff;
}

.terms-loading {
  display: flex;
  justify-content: center;
  padding: 30px 0;
}

.terms-error {
  margin: 0;
  padding: 20px 0;
  text-align: center;
  font-size: 13px;
}

.missing-list {
  text-align: left;
  margin: 0 0 18px;
  padding-left: 22px;
  font-size: 12.5px;
  line-height: 1.7;
}

.terms-check {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  text-align: left;
  margin: 0 0 18px;
  cursor: pointer;
  font-size: 12px;
  line-height: 1.5;
  color: var(--color-theater-text);
}

.terms-check input[type="checkbox"] {
  margin-top: 2px;
  width: 16px;
  height: 16px;
  accent-color: #c8102e;
  cursor: pointer;
  flex: 0 0 auto;
}

.terms-link {
  color: #ff6b6b;
  text-decoration: underline;
  cursor: pointer;
}

.terms-link:hover {
  color: #ff8a8a;
}

/* El modal de texto va por encima del formulario de aceptación. */
.terms-text-overlay {
  z-index: 60;
}

.terms-dialog {
  max-width: 560px;
}

.terms-body {
  max-height: 55vh;
  overflow-y: auto;
  text-align: left;
  margin: 0 0 18px;
  padding-right: 6px;
  font-size: 12.5px;
  line-height: 1.6;
  color: var(--color-theater-text-muted);
}

.terms-body p {
  margin: 0 0 12px;
}
</style>
